import { useState, useRef, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useFreemiumAccess } from '@/hooks/useFreemiumAccess';
import { useAICredits } from '@/hooks/useAICredits';
import { ArrowUp, ArrowLeft, Coins, ImageIcon, Video, PenLine, Search, CalendarDays, Globe, Plus, Loader2, Copy, Check, Download, X, Sparkles } from 'lucide-react';
import { Link, Navigate } from 'react-router-dom';
import LightLogo from '@/components/LightLogo';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';
import { useAITool, AIToolType } from '@/hooks/useAITool';
import { CREDIT_COSTS } from '@/hooks/useAICredits';
import ReactMarkdown from 'react-markdown';
import { toast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import BuyCreditsDialog from '@/components/ai-tools/BuyCreditsDialog';

/* ── Tool definitions ── */
const tools = [
  {
    id: 'image-creator',
    label: 'Image Creator',
    icon: ImageIcon,
    type: 'image-creator' as const,
    placeholder: 'Describe the image you want to create…',
    cost: 2,
  },
  {
    id: 'video-script',
    label: 'Video Script',
    icon: Video,
    type: 'video-script' as AIToolType,
    placeholder: 'Describe your video topic or niche…',
    cost: CREDIT_COSTS['video-script'],
  },
  {
    id: 'caption-hook',
    label: 'Captions & Hooks',
    icon: PenLine,
    type: 'caption-hook' as AIToolType,
    placeholder: 'Enter your post topic or idea…',
    cost: CREDIT_COSTS['caption-hook'],
  },
  {
    id: 'niche-analyzer',
    label: 'Niche Analyzer',
    icon: Search,
    type: 'niche-analyzer' as AIToolType,
    placeholder: 'Enter a niche to analyze…',
    cost: CREDIT_COSTS['niche-analyzer'],
  },
  {
    id: 'content-planner',
    label: 'Content Planner',
    icon: CalendarDays,
    type: 'content-planner' as AIToolType,
    placeholder: 'Enter your niche and goals…',
    cost: CREDIT_COSTS['content-planner'],
  },
  {
    id: 'website-builder',
    label: 'Website Builder',
    icon: Globe,
    type: 'website-builder' as const,
    placeholder: '',
    cost: 0,
    isExternal: true,
  },
] as const;

/* ── Image presets for Image Creator ── */
const imagePresets = [
  { id: 'instagram-story', label: 'Story 9:16' },
  { id: 'instagram-post', label: 'Post 1:1' },
  { id: 'creative', label: 'Creative' },
];

const AITools = () => {
  const { user, loading: authLoading } = useAuth();
  const { hasFullAccess, loading: accessLoading } = useFreemiumAccess();
  const { balance, monthlyAllowance, totalUsed, loading: creditsLoading } = useAICredits();
  const [localBalance, setLocalBalance] = useState<number | null>(null);
  const [showBuyCredits, setShowBuyCredits] = useState(false);

  const [activeTool, setActiveTool] = useState('image-creator');
  const [input, setInput] = useState('');
  const [imagePreset, setImagePreset] = useState('instagram-post');

  // Text tool state
  const { result: textResult, isLoading: textLoading, generate: textGenerate, clear: textClear } = useAITool();
  
  // Image tool state
  const [imageLoading, setImageLoading] = useState(false);
  const [generatedImage, setGeneratedImage] = useState<string | null>(null);
  
  const [copied, setCopied] = useState(false);
  const [submittedPrompt, setSubmittedPrompt] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const resultRef = useRef<HTMLDivElement>(null);

  const displayBalance = localBalance ?? balance ?? 0;
  const isPaid = hasFullAccess;
  const activeToolData = tools.find(t => t.id === activeTool)!;
  const isLoading = activeTool === 'image-creator' ? imageLoading : textLoading;
  const hasResult = activeTool === 'image-creator' ? !!generatedImage : !!textResult;

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 200) + 'px';
    }
  }, [input]);

  // Scroll to result
  useEffect(() => {
    if (hasResult && resultRef.current) {
      resultRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [textResult, generatedImage]);

  // Clear results when switching tools
  useEffect(() => {
    setInput('');
    setInput('');
    textClear();
    setGeneratedImage(null);
    setCopied(false);
    setSubmittedPrompt('');
  }, [activeTool]);

  if (authLoading || accessLoading || creditsLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-6 h-6 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;

  const handleCreditsUpdate = (b: number) => setLocalBalance(b);

  const handleSubmit = async () => {
    if (!input.trim() || isLoading) return;
    setSubmittedPrompt(input.trim());
    
    if (activeTool === 'image-creator') {
      handleImageGenerate();
    } else if (activeTool === 'website-builder') {
      return;
    } else {
      const cost = activeToolData.cost;
      if (displayBalance < cost) {
        toast({ title: 'Not enough credits', description: `This tool costs ${cost} credit${cost > 1 ? 's' : ''}.`, variant: 'destructive' });
        setSubmittedPrompt('');
        return;
      }
      textGenerate(activeToolData.type as AIToolType, input.trim(), isPaid, handleCreditsUpdate);
    }
  };

  const handleImageGenerate = async () => {
    if (input.trim().length < 3) return;
    if (displayBalance < 2) {
      toast({ title: 'Not enough credits', description: 'Image generation costs 2 credits.', variant: 'destructive' });
      return;
    }
    setImageLoading(true);
    setGeneratedImage(null);
    try {
      const { data, error } = await supabase.functions.invoke('ai-image-generator', {
        body: { prompt: input.trim(), preset: imagePreset },
      });
      if (error) throw new Error(typeof error === 'object' && 'message' in error ? error.message : String(error));
      if (data?.error) {
        if (data.error === 'insufficient_credits') {
          toast({ title: 'Not enough credits', description: `You have ${data.balance}, need ${data.required}.`, variant: 'destructive' });
          return;
        }
        throw new Error(data.error);
      }
      if (data?.imageUrl) {
        setGeneratedImage(data.imageUrl);
        if (data.creditsRemaining !== undefined) handleCreditsUpdate(data.creditsRemaining);
      }
    } catch (err: any) {
      toast({ title: 'Error', description: err.message || 'Failed to generate image', variant: 'destructive' });
    } finally {
      setImageLoading(false);
    }
  };

  const handleCopy = async () => {
    await navigator.clipboard.writeText(textResult);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownload = async () => {
    if (!generatedImage) return;
    try {
      const response = await fetch(generatedImage);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `aura-ai-${Date.now()}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      window.open(generatedImage, '_blank');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const clearAll = () => {
    setInput('');
    textClear();
    setGeneratedImage(null);
    setCopied(false);
    setSubmittedPrompt('');
  };

  return (
    <>
      <Helmet>
        <title>AI Tools – Faceless Aura Club</title>
        <meta name="description" content="AI-powered tools for faceless content creators." />
      </Helmet>

      <div className="min-h-screen bg-background flex flex-col">
        {/* ── Top bar ── */}
        <header className="border-b border-border/40 bg-background/80 backdrop-blur-xl sticky top-0 z-50">
          <div className="max-w-screen-xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Link to="/dashboard" className="p-1.5 -ml-1.5 rounded-lg hover:bg-muted/80 transition-colors">
                <ArrowLeft className="w-4 h-4 text-muted-foreground" />
              </Link>
              <div className="h-5 w-px bg-border/50" />
              <LightLogo size="sm" />
              <div className="h-5 w-px bg-border/50 hidden sm:block" />
              <span className="text-sm font-medium text-foreground hidden sm:block">AI Tools</span>
            </div>
            <button
              onClick={() => setShowBuyCredits(true)}
              className="group flex items-center gap-2 h-8 px-3 rounded-full border border-border/60 hover:border-primary/30 transition-all"
            >
              <div className="w-4 h-4 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center">
                <Coins className="w-2.5 h-2.5 text-white" />
              </div>
              <span className="text-sm font-semibold tabular-nums">{displayBalance}</span>
              <Plus className="w-3 h-3 text-muted-foreground group-hover:text-primary transition-colors" />
            </button>
          </div>
        </header>

        {/* ── Main content ── */}
        <div className="flex-1 flex flex-col">
          {/* Scrollable results area */}
          <div className="flex-1 overflow-y-auto">
            <div className={cn(
              'flex flex-col items-center transition-all duration-500',
              hasResult || isLoading ? 'justify-start pt-6 sm:pt-10' : 'justify-center min-h-full'
            )}>
              {/* Hero heading — only when no result */}
              <AnimatePresence>
                {!hasResult && !isLoading && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10, transition: { duration: 0.15 } }}
                    className="text-center mb-8 px-4 pt-[20vh]"
                  >
                    <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-foreground tracking-tight">
                      What do you want to create?
                    </h1>
                    <p className="text-muted-foreground text-sm sm:text-base mt-2 max-w-md mx-auto">
                      Select a tool below and describe what you need.
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* ── Result area ── */}
              <AnimatePresence>
                {(hasResult || isLoading) && (
                  <motion.div
                    ref={resultRef}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    className="w-full max-w-2xl px-4 pb-6"
                  >
              {/* ── User message bubble ── */}
              {submittedPrompt && (
                <div className="flex justify-end mb-4">
                  <div className="max-w-[85%] bg-foreground text-background rounded-2xl rounded-br-md px-4 py-3">
                    <p className="text-sm leading-relaxed">{submittedPrompt}</p>
                  </div>
                </div>
              )}

              {/* ── AI response ── */}
              <div className="flex gap-3 mb-4">
                {/* AI avatar */}
                <div className="w-7 h-7 rounded-full bg-gradient-to-br from-primary/20 to-accent/20 border border-border/60 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Sparkles className="w-3.5 h-3.5 text-primary" />
                </div>

                <div className="flex-1 min-w-0">
                  {/* Text results */}
                  {textResult && activeTool !== 'image-creator' && (
                    <div className="relative group">
                      <div className="prose prose-sm dark:prose-invert max-w-none">
                        <ReactMarkdown>{textResult}</ReactMarkdown>
                        {textLoading && (
                          <span className="inline-block w-[2px] h-[1.1em] bg-foreground align-middle ml-0.5 animate-[cursor-blink_1s_steps(2)_infinite]" />
                        )}
                      </div>
                      {!textLoading && (
                        <div className="flex gap-1.5 mt-3 pt-2 border-t border-border/40">
                          <button
                            onClick={handleCopy}
                            className="h-7 px-2.5 rounded-lg text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-muted/60 flex items-center gap-1.5 transition-colors"
                          >
                            {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                            {copied ? 'Copied' : 'Copy'}
                          </button>
                          <button
                            onClick={clearAll}
                            className="h-7 px-2.5 rounded-lg text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-muted/60 flex items-center gap-1.5 transition-colors"
                          >
                            <X className="w-3 h-3" />
                            Clear
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Image result */}
                  {generatedImage && activeTool === 'image-creator' && (
                    <div className="space-y-3">
                      <div className="relative rounded-2xl overflow-hidden border border-border/60 bg-muted max-w-md">
                        <img src={generatedImage} alt="AI Generated" className="w-full h-auto object-contain max-h-[50vh]" />
                      </div>
                      <div className="flex gap-1.5">
                        <button onClick={handleDownload} className="h-7 px-2.5 rounded-lg text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-muted/60 flex items-center gap-1.5 transition-colors">
                          <Download className="w-3 h-3" /> Download
                        </button>
                        <button onClick={clearAll} className="h-7 px-2.5 rounded-lg text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-muted/60 flex items-center gap-1.5 transition-colors">
                          <X className="w-3 h-3" /> New
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Loading: image */}
                  {isLoading && !textResult && activeTool === 'image-creator' && (
                    <div className="rounded-2xl border border-border/60 bg-muted/30 w-64 h-64 flex items-center justify-center">
                      <div className="text-center space-y-3">
                        <Loader2 className="w-6 h-6 text-muted-foreground/50 mx-auto animate-spin" />
                        <p className="text-xs text-muted-foreground">Creating your image…</p>
                      </div>
                    </div>
                  )}

                  {/* Loading: text */}
                  {textLoading && !textResult && activeTool !== 'image-creator' && (
                    <div className="flex items-center gap-2 py-1">
                      <span className="flex gap-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/40 animate-[bounce_1.4s_ease-in-out_infinite]" />
                        <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/40 animate-[bounce_1.4s_ease-in-out_0.2s_infinite]" />
                        <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/40 animate-[bounce_1.4s_ease-in-out_0.4s_infinite]" />
                      </span>
                    </div>
                  )}
                </div>
              </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* ── Fixed bottom: Input bar + tool chips ── */}
          <div className="sticky bottom-0 bg-gradient-to-t from-background via-background to-background/0 pt-6 pb-4">
            <div className="flex flex-col items-center">
              {/* Input bar */}
              <div className="w-full max-w-2xl px-4">
                {activeTool === 'website-builder' ? (
                  <WebsiteBuilderCard />
                ) : (
                  <div className="rounded-2xl border border-border/60 bg-card/80 backdrop-blur-sm shadow-lg shadow-foreground/[0.03] overflow-hidden">
                    {/* Image preset bar */}
                    {activeTool === 'image-creator' && (
                      <div className="flex items-center gap-1 px-3 pt-3 pb-0">
                        {imagePresets.map(p => (
                          <button
                            key={p.id}
                            onClick={() => setImagePreset(p.id)}
                            className={cn(
                              'text-xs font-medium px-3 py-1.5 rounded-lg transition-all',
                              imagePreset === p.id
                                ? 'bg-foreground text-background'
                                : 'text-muted-foreground hover:text-foreground hover:bg-muted/60'
                            )}
                          >
                            {p.label}
                          </button>
                        ))}
                        <div className="ml-auto text-[11px] text-muted-foreground/60 font-medium pr-1">
                          {activeToolData.cost} cr
                        </div>
                      </div>
                    )}

                    {/* Textarea */}
                    <div className="relative flex items-end">
                      <textarea
                        ref={textareaRef}
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder={activeToolData.placeholder}
                        disabled={isLoading}
                        rows={1}
                        className={cn(
                          'flex-1 resize-none bg-transparent border-0 outline-none px-4 py-4 text-sm text-foreground placeholder:text-muted-foreground/50 disabled:opacity-50',
                          'min-h-[52px] max-h-[200px]'
                        )}
                      />
                      <div className="pr-3 pb-3 flex items-center gap-2">
                        {activeTool !== 'image-creator' && (
                          <span className="text-[11px] text-muted-foreground/50 font-medium whitespace-nowrap">
                            {activeToolData.cost} cr
                          </span>
                        )}
                        <button
                          onClick={handleSubmit}
                          disabled={isLoading || !input.trim()}
                          className={cn(
                            'w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-200',
                            input.trim() && !isLoading
                              ? 'bg-foreground text-background hover:bg-foreground/90 shadow-sm'
                              : 'bg-muted text-muted-foreground/40 cursor-not-allowed'
                          )}
                        >
                          {isLoading ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <ArrowUp className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Tool selector chips */}
              <div className="flex flex-wrap justify-center gap-2 mt-3 px-4">
                {tools.map(tool => {
                  const Icon = tool.icon;
                  const isActive = activeTool === tool.id;
                  return (
                    <button
                      key={tool.id}
                      onClick={() => setActiveTool(tool.id)}
                      className={cn(
                        'flex items-center gap-2 h-9 px-4 rounded-full text-sm font-medium transition-all duration-200 border',
                        isActive
                          ? 'bg-foreground text-background border-foreground shadow-md'
                          : 'bg-card/80 text-muted-foreground border-border/60 hover:text-foreground hover:border-border hover:shadow-sm'
                      )}
                    >
                      <Icon className="w-3.5 h-3.5" />
                      {tool.label}
                    </button>
                  );
                })}
              </div>

              {/* Credits info */}
              <p className="text-[11px] text-muted-foreground/50 mt-2">
                📅 Monthly plan: 100 credits/mo · ♾️ Lifetime plan: 500 credits included
              </p>
            </div>
          </div>
        </div>
      </div>

      <BuyCreditsDialog open={showBuyCredits} onOpenChange={setShowBuyCredits} />
    </>
  );
};

/* ── Website Builder card ── */
const WebsiteBuilderCard = () => (
  <div className="rounded-2xl border border-border/60 bg-card/80 backdrop-blur-sm p-6 sm:p-8 text-center">
    <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-indigo-500/10 to-purple-500/10 border border-indigo-500/10 flex items-center justify-center mx-auto mb-4">
      <Globe className="w-6 h-6 text-indigo-500" />
    </div>
    <h3 className="text-lg font-bold text-foreground mb-2">Build Your Website with AI</h3>
    <p className="text-sm text-muted-foreground max-w-sm mx-auto mb-5">
      Describe what you want in plain English — AI builds and deploys it instantly. No code needed.
    </p>
    <a
      href="https://lovable.dev/invite/GL1X53G"
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-foreground text-background font-semibold text-sm hover:bg-foreground/90 transition-colors"
    >
      Start Building →
    </a>
    <p className="text-[11px] text-muted-foreground mt-2">Free plan available · No credit card required</p>
  </div>
);

export default AITools;
